/**
 * Phase 2e — Employee application (9-screen flow)
 *
 * Route: /apply/form?invite={token}
 *
 * Screens:
 *   1  Welcome / eligibility verification
 *   2  Coverage amount (25/50/75/100k pills)
 *   3  Tobacco status
 *   4  Rider elections (waiver, ADB, spouse, child)
 *   5  Chronic-care moment (informational + ack)
 *   6  Three health questions (any YES → declined)
 *   7  Beneficiaries (primary must sum to 100)
 *   8  Review + attestation
 *   9  Typed-signature + submit
 *  10  Done / confirmation
 *  6b  Declined-health compassionate redirect
 *
 * All state is server-side; every field edit fires a debounced PATCH and a
 * Continue → fires /advance. Back decrements current_step.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ArrowIcon } from "../components/ArrowIcon";
import I18nText from "../components/I18nText";
import PortalChrome from "../components/portal/PortalChrome";
import Term from "../components/Term";
import {
    API,
    employeeApplyAdvance,
    employeeApplyBack,
    employeeApplyGet,
    employeeApplyPatch,
    employeeApplyStart,
    employeeApplySubmit,
    employeeApplicationPdfUrl,
    formatApiError,
} from "../lib/api";
import { track } from "../lib/analytics";
import { titleCaseName } from "../lib/dataHygiene";
import "./EmployeeApply.css";

const COVERAGE_TIERS = [25000, 50000, 75000, 100000];
const DEPENDENT_TIERS = [10000, 20000];
const RELATIONSHIPS = ["spouse", "partner", "parent", "child", "sibling", "trust", "estate", "other"];

const fmt0 = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);
const fmt2 = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function useDebouncedCallback(fn, delay = 500) {
    const ref = useRef(fn);
    ref.current = fn;
    const timer = useRef(null);
    return (...args) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => ref.current(...args), delay);
    };
}

export default function EmployeeApply() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get("invite");

    const [loading, setLoading] = useState(true);
    const [bootErr, setBootErr] = useState(null);
    const [app, setApp] = useState(null);
    const [advancing, setAdvancing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    // Phase 3b §3.6 — cache the employee_id so post-submit / post-decline
    // navigates land on the specific portal URL, not the session-resolved
    // root. Non-blocking: failure falls back to /portal, which still works.
    const [portalEmployeeId, setPortalEmployeeId] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const { employeePortalMe } = await import("../lib/api");
                const me = await employeePortalMe();
                if (me?.employee_id) setPortalEmployeeId(me.employee_id);
            } catch (_) { /* portal cookie might not be set yet — fall back */ }
        })();
    }, []);
    const portalHomeUrl = portalEmployeeId
        ? `/portal/employee/${portalEmployeeId}`
        : "/portal";

    // Bootstrap: start or resume.
    useEffect(() => {
        let cancelled = false;
        if (!inviteToken) {
            setBootErr(t("apply.hub.err.invalidEmail")); // generic fallback
            setLoading(false);
            return () => { cancelled = true; };
        }
        (async () => {
            try {
                // Start is idempotent — if a row exists and is still editable, it's returned as-is.
                const res = await employeeApplyStart(inviteToken);
                if (cancelled) return;
                setApp(res.application);
                track("employee_apply_start", {
                    application_id: res.application.application_id,
                    current_step: res.application.current_step,
                    status: res.application.status,
                });
            } catch (e) {
                if (cancelled) return;
                // Phase 3b — bad token bounces to /portal (session-resolved).
                // If the employee has a valid portal cookie they'll land at
                // their dashboard; if not, /portal/signin will pick up.
                const status = e?.response?.status;
                if (status === 410 || status === 404) {
                    navigate(portalHomeUrl, { replace: true });
                    return;
                }
                setBootErr(formatApiError(e, t("employeeApply.bootErr")));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [inviteToken]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        document.title = t("employeeApply.pageTitle");
    }, [t]);

    const saveStep = async (step, payload) => {
        setSaving(true);
        setErr(null);
        try {
            const res = await employeeApplyPatch(step, payload);
            setApp(res.application);
        } catch (e) {
            setErr(formatApiError(e, t("employeeApply.saveErr")));
        } finally {
            setSaving(false);
        }
    };
    const debouncedSave = useDebouncedCallback(saveStep, 450);

    const advance = async () => {
        if (!app) return;
        setAdvancing(true);
        setErr(null);
        try {
            const res = await employeeApplyAdvance();
            track("employee_apply_advance", {
                from_step: app.current_step,
                to_step: res.application.current_step,
                declined_health: Boolean(res.declined_health),
            });
            setApp(res.application);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(
                (d && d.errors && d.errors.join(" · ")) ||
                formatApiError(e, t("employeeApply.advanceErr"))
            );
        } finally {
            setAdvancing(false);
        }
    };

    const goBack = async () => {
        if (!app || app.current_step <= 1) return;
        setAdvancing(true);
        try {
            const res = await employeeApplyBack();
            setApp(res.application);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            setErr(formatApiError(e, t("employeeApply.backErr")));
        } finally {
            setAdvancing(false);
        }
    };

    const jumpEdit = async (targetStep) => {
        if (!app) return;
        const current = app.current_step || 1;
        if (targetStep === current) return;
        setAdvancing(true);
        try {
            // We can only navigate back via /back; chain back() calls until target.
            let s = current;
            let newApp = app;
            while (s > targetStep) {
                const res = await employeeApplyBack();
                newApp = res.application;
                s = newApp.current_step;
            }
            setApp(newApp);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            setErr(formatApiError(e, t("employeeApply.backErr")));
        } finally {
            setAdvancing(false);
        }
    };

    // Phase 3 shell consolidation — wrap every return state in
    // <PortalChrome actorType="employee">. This is the anonymous chrome
    // variant: no displayName (pre-enrollment session identity isn't
    // resolved this pass), and sign-out points at "/" since there's no
    // portal session to clear. Future: resolve session from invite token.
    const ChromeWrap = ({ children: inner }) => (
        <PortalChrome actorType="employee" signOutHref={null}>
            {inner}
        </PortalChrome>
    );

    if (loading) {
        return (
            <ChromeWrap>
                <section className="ea-wrap" data-testid="employee-apply-loading">
                    <div className="ea-spinner">{t("employeeApply.loading")}</div>
                </section>
            </ChromeWrap>
        );
    }
    if (bootErr || !app) {
        return (
            <ChromeWrap>
                <section className="ea-wrap" data-testid="employee-apply-error">
                    <h1>{t("employeeApply.bootErrTitle")}</h1>
                    <p>{bootErr || t("employeeApply.bootErrBody")}</p>
                    <button type="button" className="ea-btn ea-btn-primary"
                            onClick={() => navigate("/apply")}>
                        {t("employeeApply.backToApply")}
                        <ArrowIcon direction="right" />
                    </button>
                </section>
            </ChromeWrap>
        );
    }

    // Post-submit confirmation page
    if (app.status === "submitted") {
        return <ChromeWrap><DoneScreen app={app} inviteToken={inviteToken} /></ChromeWrap>;
    }
    // Declined-health compassionate screen
    if (app.status === "declined_health") {
        return <ChromeWrap><DeclinedScreen app={app} inviteToken={inviteToken} /></ChromeWrap>;
    }

    const step = app.current_step || 1;
    return (
        <ChromeWrap>
            <section className="ea-wrap" data-testid="employee-apply">
                <ProgressBar step={step} total={9} saving={saving} lastSavedAt={app.last_saved_at} />

                {step === 1 && <Screen1 app={app} />}
                {step === 2 && <Screen2 app={app} onChange={debouncedSave} />}
                {step === 3 && <Screen3 app={app} onChange={(payload) => saveStep(3, payload)} />}
                {step === 4 && <Screen4 app={app} onChange={debouncedSave} />}
                {step === 5 && <Screen5 app={app} onChange={(payload) => saveStep(5, payload)} />}
                {step === 6 && <Screen6 app={app} onChange={(payload) => saveStep(6, payload)} />}
                {step === 7 && <Screen7 app={app} onChange={debouncedSave} />}
                {step === 8 && <Screen8 app={app} onChange={(payload) => saveStep(8, payload)} onEdit={jumpEdit} />}
                {step === 9 && <Screen9 app={app} onChange={(payload) => saveStep(9, payload)} />}

                {err && (
                    <div className="ea-err" role="alert" data-testid="ea-err">{err}</div>
                )}

                <Chrome
                    step={step}
                    app={app}
                    advancing={advancing}
                    onBack={goBack}
                    onNext={advance}
                />
            </section>
        </ChromeWrap>
    );
}

/* ---------- Common chrome: progress + bottom nav ------------------------ */
function ProgressBar({ step, total, saving, lastSavedAt }) {
    const { t } = useTranslation();
    const pct = Math.round(((step - 1) / total) * 100);
    return (
        <div className="ea-progress" data-testid="ea-progress">
            <div className="ea-progress-left">
                <span className="ea-progress-count mono" data-testid="ea-step-count">
                    {t("employeeApply.stepCount", { n: step, total })}
                </span>
                <div className="ea-progress-dots">
                    {Array.from({ length: total }).map((_, i) => (
                        <span
                            key={i}
                            className={`ea-dot ${i + 1 < step ? "is-done" : i + 1 === step ? "is-current" : ""}`}
                        />
                    ))}
                </div>
            </div>
            <div
                className={`ea-save-indicator mono ${saving ? "is-saving" : ""}`}
                data-testid="ea-save-indicator"
            >
                {saving ? t("employeeApply.saving") : t("employeeApply.savedIndicator")}
            </div>
        </div>
    );
}

function Chrome({ step, app, advancing, onBack, onNext }) {
    const { t } = useTranslation();
    const isLast = step === 9;
    // Screen 5 (chronic-care moment) uses its own inline CTA.
    return (
        <div className="ea-chrome" data-testid="ea-chrome">
            <button
                type="button"
                className="ea-btn ea-btn-ghost"
                onClick={onBack}
                disabled={step <= 1 || advancing}
                data-testid="ea-back"
            >
                <ArrowIcon direction="left" /> {t("employeeApply.backBtn")}
            </button>
            <button
                type="button"
                className="ea-btn ea-btn-primary"
                onClick={onNext}
                disabled={advancing}
                data-testid="ea-next"
            >
                {advancing
                    ? t("employeeApply.advancing")
                    : (isLast ? t("employeeApply.submitBtn") : t("employeeApply.nextBtn"))}
                <ArrowIcon direction="right" />
            </button>
        </div>
    );
}

/* ---------- Screen 1: Welcome / eligibility verification --------------- */
function Screen1({ app }) {
    const { t } = useTranslation();
    return (
        <div className="ea-screen ea-screen-1" data-testid="ea-screen-1">
            <div className="ea-kicker mono">{t("employeeApply.s1.kicker")}</div>
            <h1 className="ea-h1">
                <I18nText i18nKey="employeeApply.s1.h1" values={{ firstName: app.first_name }} />
            </h1>
            <p className="ea-lede">
                <I18nText i18nKey="employeeApply.s1.lede" values={{ employer: app.employer_name }} />
            </p>

            <div className="ea-confirm-panel" data-testid="ea-confirm-panel">
                <div className="ea-confirm-label mono">{t("employeeApply.s1.confirmLabel")}</div>
                <dl className="ea-confirm-grid">
                    <dt>{t("employeeApply.s1.fields.firstName")}</dt>
                    <dd>{app.first_name}</dd>
                    <dt>{t("employeeApply.s1.fields.lastName")}</dt>
                    <dd>{app.last_name}</dd>
                    <dt>{t("employeeApply.s1.fields.email")}</dt>
                    <dd>{app.email}</dd>
                    <dt>{t("employeeApply.s1.fields.employer")}</dt>
                    <dd>{app.employer_name}</dd>
                    <dt>{t("employeeApply.s1.fields.age")}</dt>
                    <dd>{app.age}</dd>
                </dl>
                <div className="ea-correction-link" data-testid="ea-correction-hint">
                    {t("employeeApply.s1.correctionPrompt")}
                </div>
            </div>
        </div>
    );
}

/* ---------- Screen 2: Coverage amount ---------------------------------- */
function Screen2({ app, onChange }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(app.coverage_tier || 50000);
    const premium = app.computed_premium || {};
    const pick = (tier) => {
        setSelected(tier);
        onChange(2, { coverage_tier: tier });
    };
    return (
        <div className="ea-screen" data-testid="ea-screen-2">
            <div className="ea-kicker mono">{t("employeeApply.s2.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s2.h1" /></h1>
            <p className="ea-lede">
                <I18nText i18nKey="employeeApply.s2.lede" />{" "}
                <Term term="coverageAmount" icon />
            </p>
            <div className="ea-coverage-pills" data-testid="ea-coverage-pills">
                {COVERAGE_TIERS.map((v) => (
                    <button
                        key={v}
                        type="button"
                        className={`ea-pill ${selected === v ? "is-active" : ""}`}
                        onClick={() => pick(v)}
                        data-testid={`ea-pill-${v}`}
                    >
                        <div className="ea-pill-big">{fmt0(v)}</div>
                        <div className="ea-pill-sub mono">
                            {t("employeeApply.s2.pillSub")}
                        </div>
                    </button>
                ))}
            </div>

            <div className="ea-live-panel" data-testid="ea-live-panel">
                <div className="ea-live-row">
                    <span className="ea-live-label mono">
                        {t("employeeApply.s2.liveCost")}
                    </span>
                    <span className="ea-live-value" data-testid="ea-live-cost">
                        {fmt2(premium.bi_weekly)} <span className="ea-live-cadence">{t("employeeApply.s2.cadence")}</span>
                    </span>
                </div>
                <div className="ea-live-row">
                    <span className="ea-live-label mono">
                        {t("employeeApply.s2.liveCash")}
                    </span>
                    <span className="ea-live-value">{fmt0(selected * 0.5)}</span>
                </div>
                <div className="ea-live-row">
                    <span className="ea-live-label mono">
                        {t("employeeApply.s2.liveDb")}
                    </span>
                    <span className="ea-live-value">{fmt0(selected)}</span>
                </div>
            </div>
        </div>
    );
}

/* ---------- Screen 3: Tobacco status ----------------------------------- */
function Screen3({ app, onChange }) {
    const { t } = useTranslation();
    const [val, setVal] = useState(app.tobacco_status);
    const pick = (v) => {
        setVal(v);
        onChange({ tobacco_status: v });
    };
    return (
        <div className="ea-screen" data-testid="ea-screen-3">
            <div className="ea-kicker mono">{t("employeeApply.s3.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s3.h1" /></h1>
            <p className="ea-lede">
                {t("employeeApply.s3.lede")} <Term term="tobaccoStatus" icon />
            </p>
            <div className="ea-radio-stack">
                <button
                    type="button"
                    className={`ea-radio ${val === "non_tobacco" ? "is-selected" : ""}`}
                    onClick={() => pick("non_tobacco")}
                    data-testid="ea-tobacco-no"
                >
                    <span className="ea-radio-ring" />
                    <div>
                        <div className="ea-radio-title">{t("employeeApply.s3.noTitle")}</div>
                        <div className="ea-radio-sub">{t("employeeApply.s3.noSub")}</div>
                    </div>
                </button>
                <button
                    type="button"
                    className={`ea-radio ${val === "tobacco" ? "is-selected" : ""}`}
                    onClick={() => pick("tobacco")}
                    data-testid="ea-tobacco-yes"
                >
                    <span className="ea-radio-ring" />
                    <div>
                        <div className="ea-radio-title">{t("employeeApply.s3.yesTitle")}</div>
                        <div className="ea-radio-sub">{t("employeeApply.s3.yesSub")}</div>
                    </div>
                </button>
            </div>
        </div>
    );
}

/* ---------- Screen 4: Rider elections ---------------------------------- */
function Screen4({ app, onChange }) {
    const { t } = useTranslation();
    const enabled = app.employer_rider_enablement || {};
    const [r, setR] = useState(app.rider_elections || {});
    const commit = (next) => {
        setR(next);
        onChange(4, { rider_elections: next });
    };
    const toggle = (k) => commit({ ...r, [k]: !r[k] });
    const toggleSpouse = () => commit({
        ...r,
        spouse_coverage: { ...(r.spouse_coverage || {}), enabled: !(r.spouse_coverage?.enabled) },
    });
    const pickSpouseTier = (v) => commit({
        ...r, spouse_coverage: { ...(r.spouse_coverage || {}), coverage_tier: v },
    });
    const setSpouseField = (field, val) => commit({
        ...r, spouse_coverage: { ...(r.spouse_coverage || {}), [field]: val },
    });
    const toggleChild = () => commit({
        ...r,
        child_coverage: {
            ...(r.child_coverage || {}),
            enabled: !(r.child_coverage?.enabled),
            children: r.child_coverage?.children?.length ? r.child_coverage.children : [{ first_name: "", last_name: "", dob: "" }],
        },
    });
    const pickChildTier = (v) => commit({
        ...r, child_coverage: { ...(r.child_coverage || {}), coverage_tier: v },
    });
    const setChildField = (i, field, val) => {
        const kids = [...(r.child_coverage?.children || [])];
        kids[i] = { ...kids[i], [field]: val };
        commit({ ...r, child_coverage: { ...(r.child_coverage || {}), children: kids } });
    };
    const addChild = () => {
        const kids = [...(r.child_coverage?.children || []), { first_name: "", last_name: "", dob: "" }];
        commit({ ...r, child_coverage: { ...(r.child_coverage || {}), children: kids } });
    };
    const removeChild = (i) => {
        const kids = (r.child_coverage?.children || []).filter((_, idx) => idx !== i);
        commit({ ...r, child_coverage: { ...(r.child_coverage || {}), children: kids } });
    };

    const premium = app.computed_premium || {};

    return (
        <div className="ea-screen" data-testid="ea-screen-4">
            <div className="ea-kicker mono">{t("employeeApply.s4.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s4.h1" /></h1>
            <p className="ea-lede">
                {t("employeeApply.s4.lede")} <Term term="rider" icon />
            </p>

            <div className="ea-rider-row" data-testid="ea-rider-wp">
                <label className="ea-toggle">
                    <input type="checkbox" checked={!!r.waiver_of_premium}
                           onChange={() => toggle("waiver_of_premium")}
                           data-testid="ea-wp-input" />
                    <span className="ea-toggle-slider" aria-hidden="true" />
                </label>
                <div>
                    <div className="ea-rider-title">
                        {t("employeeApply.s4.wpTitle")}
                    </div>
                    <div className="ea-rider-sub">{t("employeeApply.s4.wpSub")}</div>
                </div>
            </div>

            <div className="ea-rider-row" data-testid="ea-rider-adb">
                <label className="ea-toggle">
                    <input type="checkbox" checked={!!r.accidental_death_benefit}
                           onChange={() => toggle("accidental_death_benefit")}
                           data-testid="ea-adb-input" />
                    <span className="ea-toggle-slider" aria-hidden="true" />
                </label>
                <div>
                    <div className="ea-rider-title">{t("employeeApply.s4.adbTitle")}</div>
                    <div className="ea-rider-sub">{t("employeeApply.s4.adbSub")}</div>
                </div>
            </div>

            {enabled.dependent_coverage && enabled.dependent_coverage !== "none" && enabled.dependent_coverage !== false && (
                <>
                    <div className="ea-rider-row" data-testid="ea-rider-spouse">
                        <label className="ea-toggle">
                            <input type="checkbox"
                                   checked={!!r.spouse_coverage?.enabled}
                                   onChange={toggleSpouse}
                                   data-testid="ea-spouse-input" />
                            <span className="ea-toggle-slider" aria-hidden="true" />
                        </label>
                        <div>
                            <div className="ea-rider-title">{t("employeeApply.s4.spouseTitle")}</div>
                            <div className="ea-rider-sub">{t("employeeApply.s4.spouseSub")}</div>
                            {r.spouse_coverage?.enabled && (
                                <div className="ea-dep-form">
                                    <div className="ea-tier-row">
                                        {DEPENDENT_TIERS.map((v) => (
                                            <button key={v} type="button"
                                                    className={`ea-tier-pill ${r.spouse_coverage?.coverage_tier === v ? "is-active" : ""}`}
                                                    onClick={() => pickSpouseTier(v)}
                                                    data-testid={`ea-spouse-tier-${v}`}>
                                                {fmt0(v)}
                                            </button>
                                        ))}
                                    </div>
                                    <input className="ea-input" placeholder={t("employeeApply.s4.spouseFirst")}
                                           value={r.spouse_coverage?.spouse_first_name || ""}
                                           onChange={(e) => setSpouseField("spouse_first_name", e.target.value)}
                                           onBlur={(e) => setSpouseField("spouse_first_name", titleCaseName(e.target.value))}
                                           data-testid="ea-spouse-first" />
                                    <input className="ea-input" placeholder={t("employeeApply.s4.spouseLast")}
                                           value={r.spouse_coverage?.spouse_last_name || ""}
                                           onChange={(e) => setSpouseField("spouse_last_name", e.target.value)}
                                           onBlur={(e) => setSpouseField("spouse_last_name", titleCaseName(e.target.value))}
                                           data-testid="ea-spouse-last" />
                                    <input className="ea-input" type="date"
                                           placeholder={t("employeeApply.s4.spouseDob")}
                                           value={r.spouse_coverage?.spouse_dob || ""}
                                           onChange={(e) => setSpouseField("spouse_dob", e.target.value)}
                                           data-testid="ea-spouse-dob" />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="ea-rider-row" data-testid="ea-rider-child">
                        <label className="ea-toggle">
                            <input type="checkbox"
                                   checked={!!r.child_coverage?.enabled}
                                   onChange={toggleChild}
                                   data-testid="ea-child-input" />
                            <span className="ea-toggle-slider" aria-hidden="true" />
                        </label>
                        <div>
                            <div className="ea-rider-title">{t("employeeApply.s4.childTitle")}</div>
                            <div className="ea-rider-sub">{t("employeeApply.s4.childSub")}</div>
                            {r.child_coverage?.enabled && (
                                <div className="ea-dep-form">
                                    <div className="ea-tier-row">
                                        {DEPENDENT_TIERS.map((v) => (
                                            <button key={v} type="button"
                                                    className={`ea-tier-pill ${r.child_coverage?.coverage_tier === v ? "is-active" : ""}`}
                                                    onClick={() => pickChildTier(v)}
                                                    data-testid={`ea-child-tier-${v}`}>
                                                {fmt0(v)}
                                            </button>
                                        ))}
                                    </div>
                                    {(r.child_coverage?.children || []).map((c, i) => (
                                        <div key={i} className="ea-child-row" data-testid={`ea-child-row-${i}`}>
                                            <input className="ea-input" placeholder={t("employeeApply.s4.childFirst")}
                                                   value={c.first_name || ""}
                                                   onChange={(e) => setChildField(i, "first_name", e.target.value)}
                                                   onBlur={(e) => setChildField(i, "first_name", titleCaseName(e.target.value))} />
                                            <input className="ea-input" placeholder={t("employeeApply.s4.childLast")}
                                                   value={c.last_name || ""}
                                                   onChange={(e) => setChildField(i, "last_name", e.target.value)}
                                                   onBlur={(e) => setChildField(i, "last_name", titleCaseName(e.target.value))} />
                                            <input className="ea-input" type="date"
                                                   placeholder={t("employeeApply.s4.childDob")}
                                                   value={c.dob || ""}
                                                   onChange={(e) => setChildField(i, "dob", e.target.value)} />
                                            {i > 0 && (
                                                <button type="button" className="ea-child-remove"
                                                        onClick={() => removeChild(i)}
                                                        aria-label={t("employeeApply.s4.childRemove")}>
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {(r.child_coverage?.children || []).length < 5 && (
                                        <button type="button" className="ea-child-add"
                                                onClick={addChild} data-testid="ea-child-add">
                                            + {t("employeeApply.s4.childAdd")}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            <div className="ea-live-panel" data-testid="ea-live-panel-s4">
                <div className="ea-live-row">
                    <span className="ea-live-label mono">
                        {t("employeeApply.s4.combinedCost")}
                    </span>
                    <span className="ea-live-value" data-testid="ea-s4-cost">
                        {fmt2(premium.bi_weekly)} <span className="ea-live-cadence">{t("employeeApply.s2.cadence")}</span>
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ---------- Screen 5: Chronic-care moment ------------------------------ */
function Screen5({ app, onChange }) {
    const { t } = useTranslation();
    const [ack, setAck] = useState(!!app.chronic_care_acknowledged);
    const toggle = () => {
        const v = !ack;
        setAck(v);
        onChange({ chronic_care_acknowledged: v });
    };
    return (
        <div className="ea-screen ea-screen-5 moment" data-testid="ea-screen-5">
            <div className="ea-kicker mono">{t("employeeApply.s5.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s5.h1" /></h1>
            <p className="ea-moment-para">{t("employeeApply.s5.p1")}</p>
            <p className="ea-moment-para">{t("employeeApply.s5.p2")}</p>
            <p className="ea-moment-para">
                <Term term="chronicIllnessBenefit">{t("employeeApply.s5.p3Trigger")}</Term>
                {" — "}{t("employeeApply.s5.p3Rest")}
            </p>

            <label className="ea-ack" data-testid="ea-ack-label">
                <input type="checkbox" checked={ack} onChange={toggle} data-testid="ea-ack-input" />
                <span className="ea-ack-text">{t("employeeApply.s5.ack")}</span>
            </label>
        </div>
    );
}

/* ---------- Screen 6: Three health questions --------------------------- */
function Screen6({ app, onChange }) {
    const { t } = useTranslation();
    const [ha, setHa] = useState(app.health_answers || {
        q1_hospitalized_or_bedridden: null,
        q2_confined_to_nursing_home: null,
        q3_hiv_or_terminal: null,
    });
    const pick = (q, val) => {
        const next = { ...ha, [q]: val };
        setHa(next);
        onChange({ health_answers: next });
    };
    const Q = ({ qKey, qText }) => (
        <div className="ea-qrow" data-testid={`ea-q-${qKey}`}>
            <div className="ea-qtext">{qText}</div>
            <div className="ea-yn">
                <button type="button"
                        className={`ea-yn-btn ${ha[qKey] === false ? "is-active-no" : ""}`}
                        onClick={() => pick(qKey, false)}
                        data-testid={`ea-${qKey}-no`}>
                    {t("employeeApply.s6.no")}
                </button>
                <button type="button"
                        className={`ea-yn-btn ${ha[qKey] === true ? "is-active-yes" : ""}`}
                        onClick={() => pick(qKey, true)}
                        data-testid={`ea-${qKey}-yes`}>
                    {t("employeeApply.s6.yes")}
                </button>
            </div>
        </div>
    );
    return (
        <div className="ea-screen" data-testid="ea-screen-6">
            <div className="ea-kicker mono">{t("employeeApply.s6.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s6.h1" /></h1>
            <p className="ea-lede">
                {t("employeeApply.s6.lede")}{" "}
                <Term term="simplifiedUnderwriting" icon />
            </p>
            <div className="ea-q-list">
                <Q qKey="q1_hospitalized_or_bedridden" qText={t("employeeApply.s6.q1")} />
                <Q qKey="q2_confined_to_nursing_home" qText={t("employeeApply.s6.q2")} />
                <Q qKey="q3_hiv_or_terminal" qText={t("employeeApply.s6.q3")} />
            </div>
            <div className="ea-honesty-note" data-testid="ea-honesty-note">
                {t("employeeApply.s6.honesty")}
            </div>
        </div>
    );
}

/* ---------- Screen 7: Beneficiaries ------------------------------------ */
function Screen7({ app, onChange }) {
    const { t } = useTranslation();
    const [ben, setBen] = useState(app.beneficiaries || { primary: [], contingent: [] });
    const commit = (next) => {
        setBen(next);
        onChange(7, { beneficiaries: next });
    };
    const addRow = (set) => {
        const rows = [...(ben[set] || []), { first_name: "", last_name: "", relationship: "spouse", dob: "", percent: 0 }];
        commit({ ...ben, [set]: rows });
    };
    const removeRow = (set, i) => {
        const rows = (ben[set] || []).filter((_, idx) => idx !== i);
        commit({ ...ben, [set]: rows });
    };
    const updateRow = (set, i, field, val) => {
        const rows = [...(ben[set] || [])];
        rows[i] = { ...rows[i], [field]: field === "percent" ? Math.max(0, Math.min(100, parseInt(val || "0", 10))) : val };
        commit({ ...ben, [set]: rows });
    };
    const total = (set) =>
        (ben[set] || []).reduce((s, r) => s + (parseInt(r.percent || 0, 10) || 0), 0);

    const Section = ({ set, title, subtitle, testid }) => {
        const rows = ben[set] || [];
        const sum = total(set);
        return (
            <div className="ea-ben-section" data-testid={testid}>
                <div className="ea-ben-head">
                    <h3>{title}</h3>
                    <div className="ea-ben-sub">{subtitle}</div>
                </div>
                {rows.map((r, i) => (
                    <div key={i} className="ea-ben-row" data-testid={`${testid}-row-${i}`}>
                        <input className="ea-input" placeholder={t("employeeApply.s7.firstName")}
                               value={r.first_name || ""}
                               onChange={(e) => updateRow(set, i, "first_name", e.target.value)}
                               onBlur={(e) => updateRow(set, i, "first_name", titleCaseName(e.target.value))} />
                        <input className="ea-input" placeholder={t("employeeApply.s7.lastName")}
                               value={r.last_name || ""}
                               onChange={(e) => updateRow(set, i, "last_name", e.target.value)}
                               onBlur={(e) => updateRow(set, i, "last_name", titleCaseName(e.target.value))} />
                        <select className="ea-input" value={r.relationship || "spouse"}
                                onChange={(e) => updateRow(set, i, "relationship", e.target.value)}>
                            {RELATIONSHIPS.map((rel) => (
                                <option key={rel} value={rel}>
                                    {t(`employeeApply.s7.rel.${rel}`)}
                                </option>
                            ))}
                        </select>
                        <input className="ea-input" type="number" min="0" max="100"
                               value={r.percent || 0}
                               onChange={(e) => updateRow(set, i, "percent", e.target.value)}
                               data-testid={`${testid}-pct-${i}`} />
                        <button type="button" className="ea-child-remove"
                                onClick={() => removeRow(set, i)}
                                aria-label={t("employeeApply.s7.remove")}>×</button>
                    </div>
                ))}
                {rows.length < 4 && (
                    <button type="button" className="ea-child-add" onClick={() => addRow(set)}
                            data-testid={`${testid}-add`}>
                        + {t("employeeApply.s7.add")}
                    </button>
                )}
                {rows.length > 0 && (
                    <div className={`ea-ben-total mono ${sum !== 100 ? "bad" : "good"}`}
                         data-testid={`${testid}-total`}>
                        {t("employeeApply.s7.total", { pct: sum })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="ea-screen" data-testid="ea-screen-7">
            <div className="ea-kicker mono">{t("employeeApply.s7.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s7.h1" /></h1>
            <p className="ea-lede">
                {t("employeeApply.s7.lede")}{" "}
                <Term term="beneficiary" icon />
            </p>
            <Section
                set="primary"
                title={t("employeeApply.s7.primaryTitle")}
                subtitle={t("employeeApply.s7.primarySub")}
                testid="ea-ben-primary"
            />
            <Section
                set="contingent"
                title={<>{t("employeeApply.s7.contingentTitle")} <Term term="contingentBeneficiary" icon /></>}
                subtitle={t("employeeApply.s7.contingentSub")}
                testid="ea-ben-contingent"
            />
            <div className="ea-honesty-note">
                {t("employeeApply.s7.estateNote")}
            </div>
        </div>
    );
}

/* ---------- Screen 8: Review + attestation ----------------------------- */
function Screen8({ app, onChange, onEdit }) {
    const { t } = useTranslation();
    const [att, setAtt] = useState(app.attestation || { accurate: false, authorized: false });
    const commit = (next) => {
        setAtt(next);
        onChange({ attestation: next });
    };
    const premium = app.computed_premium || {};
    const r = app.rider_elections || {};

    const RiderLine = ({ label, on, cost }) => (
        <li className={`ea-rev-rider ${on ? "on" : "off"}`}>
            <span>{label}</span>
            {on ? <span className="ea-rev-cost mono">+{fmt2(cost)}</span> : <span className="ea-rev-cost mono">—</span>}
        </li>
    );
    const spouseCost = (premium.rider_breakdown || []).find((x) => x.rider === "spouse_term_life")?.bi_weekly_cost;
    const childCost = (premium.rider_breakdown || []).find((x) => x.rider === "child_term_life")?.bi_weekly_cost;
    const wpCost = (premium.rider_breakdown || []).find((x) => x.rider === "waiver_of_premium")?.bi_weekly_cost;
    const adbCost = (premium.rider_breakdown || []).find((x) => x.rider === "accidental_death_benefit")?.bi_weekly_cost;

    return (
        <div className="ea-screen" data-testid="ea-screen-8">
            <div className="ea-kicker mono">{t("employeeApply.s8.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s8.h1" /></h1>
            <p className="ea-lede">{t("employeeApply.s8.lede")}</p>

            <div className="ea-rev-block">
                <div className="ea-rev-head">
                    <h3>{t("employeeApply.s8.coverageH")}</h3>
                    <button type="button" className="ea-rev-edit" onClick={() => onEdit(2)} data-testid="ea-edit-2">
                        {t("employeeApply.s8.edit")}
                    </button>
                </div>
                <div className="ea-rev-row"><span>{t("employeeApply.s8.amount")}</span><strong>{fmt0(app.coverage_tier)}</strong></div>
                <div className="ea-rev-row"><span>{t("employeeApply.s8.tobacco")}</span><strong>{app.tobacco_status === "tobacco" ? t("employeeApply.s8.tobYes") : t("employeeApply.s8.tobNo")}</strong></div>
            </div>

            <div className="ea-rev-block">
                <div className="ea-rev-head">
                    <h3>{t("employeeApply.s8.ridersH")}</h3>
                    <button type="button" className="ea-rev-edit" onClick={() => onEdit(4)} data-testid="ea-edit-4">
                        {t("employeeApply.s8.edit")}
                    </button>
                </div>
                <ul className="ea-rev-riders">
                    <RiderLine label={t("employeeApply.s4.wpTitle")} on={!!r.waiver_of_premium} cost={wpCost} />
                    <RiderLine label={t("employeeApply.s4.adbTitle")} on={!!r.accidental_death_benefit} cost={adbCost} />
                    <RiderLine label={t("employeeApply.s4.spouseTitle")} on={!!r.spouse_coverage?.enabled} cost={spouseCost} />
                    <RiderLine label={t("employeeApply.s4.childTitle")} on={!!r.child_coverage?.enabled} cost={childCost} />
                </ul>
            </div>

            <div className="ea-rev-block">
                <div className="ea-rev-head">
                    <h3>{t("employeeApply.s8.beneficiariesH")}</h3>
                    <button type="button" className="ea-rev-edit" onClick={() => onEdit(7)} data-testid="ea-edit-7">
                        {t("employeeApply.s8.edit")}
                    </button>
                </div>
                <ul className="ea-rev-list">
                    {((app.beneficiaries?.primary) || []).map((b, i) => (
                        <li key={`p-${i}`}>
                            <strong>{b.first_name} {b.last_name}</strong>
                            {" — "}<em>{t(`employeeApply.s7.rel.${b.relationship}`)}</em>
                            {" · "}{b.percent}%
                            <span className="ea-rev-tag mono">{t("employeeApply.s7.primaryTag")}</span>
                        </li>
                    ))}
                    {((app.beneficiaries?.contingent) || []).map((b, i) => (
                        <li key={`c-${i}`}>
                            <strong>{b.first_name} {b.last_name}</strong>
                            {" — "}<em>{t(`employeeApply.s7.rel.${b.relationship}`)}</em>
                            {" · "}{b.percent}%
                            <span className="ea-rev-tag mono contingent">{t("employeeApply.s7.contingentTag")}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="ea-rev-premium" data-testid="ea-rev-premium">
                <div className="ea-rev-premium-label mono">{t("employeeApply.s8.premiumLabel")}</div>
                <div className="ea-rev-premium-value">{fmt2(premium.bi_weekly)} <span>{t("employeeApply.s2.cadence")}</span></div>
                <div className="ea-rev-premium-alt">{fmt2(premium.monthly)} {t("employeeApply.s8.monthly")} · {fmt0(premium.annual)} {t("employeeApply.s8.annual")}</div>
            </div>

            <div className="ea-attest">
                <label className="ea-ack">
                    <input type="checkbox" checked={!!att.accurate}
                           onChange={(e) => commit({ ...att, accurate: e.target.checked })}
                           data-testid="ea-attest-accurate" />
                    <span className="ea-ack-text">{t("employeeApply.s8.attestAccurate")}</span>
                </label>
                <label className="ea-ack">
                    <input type="checkbox" checked={!!att.authorized}
                           onChange={(e) => commit({ ...att, authorized: e.target.checked })}
                           data-testid="ea-attest-authorized" />
                    <span className="ea-ack-text">{t("employeeApply.s8.attestAuthorized")}</span>
                </label>
            </div>
        </div>
    );
}

/* ---------- Screen 9: Typed signature ---------------------------------- */
function Screen9({ app, onChange }) {
    const { t } = useTranslation();
    const [name, setName] = useState(app.signature?.typed_name || "");
    const expected = `${app.first_name || ""} ${app.last_name || ""}`.trim();
    const matches = name.trim().toLowerCase() === expected.toLowerCase();
    const today = new Date().toLocaleDateString();
    return (
        <div className="ea-screen" data-testid="ea-screen-9">
            <div className="ea-kicker mono">{t("employeeApply.s9.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.s9.h1" /></h1>
            <p className="ea-lede">{t("employeeApply.s9.lede")}</p>
            <input
                className="ea-input ea-sig-input"
                placeholder={expected}
                value={name}
                onChange={(e) => { setName(e.target.value); onChange({ signature: { typed_name: e.target.value } }); }}
                data-testid="ea-sig-input"
            />
            <div className="ea-sig-date mono">{t("employeeApply.s9.dateLabel")}: {today}</div>
            {!matches && name.trim() && (
                <div className="ea-sig-hint" data-testid="ea-sig-hint">
                    {t("employeeApply.s9.mustMatch", { expected })}
                </div>
            )}
            <div className="ea-honesty-note">{t("employeeApply.s9.legal")}</div>
        </div>
    );
}

/* ---------- Screen 10: Done confirmation ------------------------------- */
function DoneScreen({ app, inviteToken }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEffect(() => {
        track("employee_apply_submitted_view", {
            application_id: app.application_id,
        });
    }, [app.application_id]);
    return (
        <section className="ea-wrap ea-done" data-testid="ea-screen-10">
            <div className="ea-kicker mono">{t("employeeApply.done.kicker")}</div>
            <h1 className="ea-h1">
                <I18nText i18nKey="employeeApply.done.h1" />
            </h1>
            <p className="ea-lede">
                <I18nText i18nKey="employeeApply.done.lede" values={{ employer: app.employer_name }} />
            </p>
            <ol className="ea-timeline">
                <li><strong>{t("employeeApply.done.t1label")}</strong> — {t("employeeApply.done.t1body")}</li>
                <li><strong>{t("employeeApply.done.t2label")}</strong> — {t("employeeApply.done.t2body")}</li>
                <li><strong>{t("employeeApply.done.t3label")}</strong> — {t("employeeApply.done.t3body")}</li>
            </ol>
            <div className="ea-done-actions">
                <a href={employeeApplicationPdfUrl(app.application_id)} target="_blank" rel="noopener noreferrer"
                   className="ea-btn ea-btn-ghost" data-testid="ea-done-pdf">
                    {t("employeeApply.done.pdfBtn")} <ArrowIcon direction="up-right" />
                </a>
                <button type="button" className="ea-btn ea-btn-primary"
                        onClick={() => navigate(portalHomeUrl)}
                        data-testid="ea-done-back">
                    {t("employeeApply.done.backBtn")} <ArrowIcon direction="right" />
                </button>
            </div>
        </section>
    );
}

/* ---------- Screen 6b: Declined-health compassionate redirect --------- */
function DeclinedScreen({ app, inviteToken }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEffect(() => {
        track("employee_apply_declined_view", { application_id: app.application_id });
    }, [app.application_id]);
    return (
        <section className="ea-wrap ea-declined" data-testid="ea-screen-declined">
            <div className="ea-kicker mono">{t("employeeApply.declined.kicker")}</div>
            <h1 className="ea-h1"><I18nText i18nKey="employeeApply.declined.h1" /></h1>
            <p className="ea-lede">{t("employeeApply.declined.p1")}</p>
            <p className="ea-lede">{t("employeeApply.declined.p2")}</p>
            <div className="ea-done-actions">
                <button
                    type="button"
                    className="ea-btn ea-btn-primary"
                    onClick={() => {
                        // Click opens the Ask Success Team FAB programmatically by dispatching a click.
                        document.querySelector('[data-testid="ask-fab"]')?.click();
                    }}
                    data-testid="ea-declined-ask"
                >
                    {t("employeeApply.declined.askBtn")} <ArrowIcon direction="right" />
                </button>
                <button
                    type="button"
                    className="ea-btn ea-btn-ghost"
                    onClick={() => navigate(portalHomeUrl)}
                    data-testid="ea-declined-back"
                >
                    <ArrowIcon direction="left" /> {t("employeeApply.declined.backBtn")}
                </button>
            </div>
        </section>
    );
}
